import {
  useRef,
  useState,
  type KeyboardEvent,
  type SyntheticEvent,
} from 'react'
import { useTranslation } from 'react-i18next'
import Icon from 'components/priority/Icon/Icon'
import { useOutsideClick } from 'hooks'
import { isUpscaleAspectSupported } from '@messenger/utils'
import { useAiImageEditsEnabled } from '@/hooks/caseImages/useAiImageEditsEnabled'
import type { CaseImageTile as CaseImageTileData } from '@/atoms/caseImageAtoms'
import {
  isLowResolution,
  type ImageDimensions,
} from '@/utils/caseImages/lowResolution'
import { LowResolutionBadge } from './LowResolutionBadge'
import * as S from './CaseImageTile.styled'
import { CASE_IMAGES_TEST_IDS } from './caseImagesTestIds'

type CaseImageTileProps = {
  tile: CaseImageTileData
  /** The first image in the grid is the case's default image. */
  isDefault: boolean
  /** True while an enhancement of this image is generating (photo pulses). */
  enhancing: boolean
  /**
   * True while any image's enhancement is generating. Every tile's Enhance
   * item is disabled then, so two paid generations can never overlap.
   */
  enhanceDisabled: boolean
  onSetDefault: (index: number) => void
  onEnhance: (tile: CaseImageTileData) => void
  onDelete: (index: number) => void
  onCompare: (tile: CaseImageTileData) => void
  onRevert: (tile: CaseImageTileData) => void
  onShowEnhancement: (tile: CaseImageTileData) => void
}

/**
 * One photo square in the case images grid. Hovering (or tabbing into it)
 * reveals the ellipses button whose dropdown holds every per-image action.
 * An accepted enhancement is marked "Edited with AI"; a small source photo
 * carries the low-resolution warning that routes into the Enhance flow.
 * While AI image edits are off (`useAiImageEditsEnabled`) the tile offers
 * neither Enhance nor Compare and draws no low-resolution badge, whose only
 * action is Enhance; neither does a photo wider or taller than the upscale
 * model takes (`isUpscaleAspectSupported`), which no Enhance could help.
 */
export const CaseImageTile = ({
  tile,
  isDefault,
  enhancing,
  enhanceDisabled,
  onSetDefault,
  onEnhance,
  onDelete,
  onCompare,
  onRevert,
  onShowEnhancement,
}: CaseImageTileProps) => {
  const { t } = useTranslation()
  const aiImageEditsEnabled = useAiImageEditsEnabled()
  const [menuOpen, setMenuOpen] = useState(false)
  const [dimensions, setDimensions] = useState<ImageDimensions | null>(null)
  const tileRef = useRef<HTMLDivElement>(null)

  useOutsideClick(tileRef, menuOpen, () => setMenuOpen(false), 'mousedown')

  const { display, index } = tile
  const editedWithAi = display.shownVariant !== null
  const enhanceable =
    aiImageEditsEnabled && isUpscaleAspectSupported(dimensions)
  const lowResolution = !enhancing && isLowResolution(dimensions)

  const onImageLoad = (event: SyntheticEvent<HTMLImageElement>) => {
    const { naturalWidth, naturalHeight } = event.currentTarget
    setDimensions({ width: naturalWidth, height: naturalHeight })
  }

  const closeMenu = () => setMenuOpen(false)

  const onTileKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape' && menuOpen) {
      event.stopPropagation()
      closeMenu()
    }
  }

  const runAndClose = (action: () => void) => () => {
    closeMenu()
    action()
  }

  return (
    <S.Tile
      ref={tileRef}
      $menuOpen={menuOpen}
      data-testid={CASE_IMAGES_TEST_IDS.TILE}
      onKeyDown={onTileKeyDown}
    >
      <S.TileImageFrame>
        <S.TileImage
          src={display.url}
          alt=""
          $pulsing={enhancing}
          onLoad={onImageLoad}
          data-testid={CASE_IMAGES_TEST_IDS.TILE_IMAGE}
        />
      </S.TileImageFrame>

      {enhanceable && lowResolution && (
        <LowResolutionBadge onEnhance={() => onEnhance(tile)} />
      )}

      <S.TileActions>
        <S.TileIconButton
          type="button"
          aria-label={t('caseImages.tile.menuLabel')}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          data-testid={CASE_IMAGES_TEST_IDS.TILE_MENU_BUTTON}
          onClick={() => setMenuOpen((open) => !open)}
        >
          <Icon icon="ellipses" fill="gray1" width="14px" height="4px" />
        </S.TileIconButton>
      </S.TileActions>

      {menuOpen && (
        <S.TileMenu role="menu" data-testid={CASE_IMAGES_TEST_IDS.TILE_MENU}>
          <S.TileMenuItem
            type="button"
            role="menuitem"
            disabled={isDefault}
            data-testid={CASE_IMAGES_TEST_IDS.TILE_SET_DEFAULT}
            onClick={runAndClose(() => onSetDefault(index))}
          >
            {t('caseImages.tile.setAsDefault')}
          </S.TileMenuItem>
          {enhanceable && (
            <S.TileMenuItem
              type="button"
              role="menuitem"
              disabled={enhancing || enhanceDisabled}
              data-testid={CASE_IMAGES_TEST_IDS.TILE_ENHANCE}
              onClick={runAndClose(() => onEnhance(tile))}
            >
              {t('caseImages.tile.enhance')}
            </S.TileMenuItem>
          )}
          {aiImageEditsEnabled && display.acceptedVariant && (
            <S.TileMenuItem
              type="button"
              role="menuitem"
              data-testid={CASE_IMAGES_TEST_IDS.TILE_COMPARE}
              onClick={runAndClose(() => onCompare(tile))}
            >
              {t('caseImages.tile.compare')}
            </S.TileMenuItem>
          )}
          {aiImageEditsEnabled && display.shownVariant && (
            <S.TileMenuItem
              type="button"
              role="menuitem"
              data-testid={CASE_IMAGES_TEST_IDS.TILE_REVERT}
              onClick={runAndClose(() => onRevert(tile))}
            >
              {t('caseImages.tile.revert')}
            </S.TileMenuItem>
          )}
          {aiImageEditsEnabled && display.reverted && (
            <S.TileMenuItem
              type="button"
              role="menuitem"
              data-testid={CASE_IMAGES_TEST_IDS.TILE_SHOW_ENHANCEMENT}
              onClick={runAndClose(() => onShowEnhancement(tile))}
            >
              {t('caseImages.enhance.showEnhancement')}
            </S.TileMenuItem>
          )}
          <S.TileMenuItem
            type="button"
            role="menuitem"
            data-testid={CASE_IMAGES_TEST_IDS.TILE_DELETE}
            onClick={runAndClose(() => onDelete(index))}
          >
            {t('caseImages.tile.delete')}
          </S.TileMenuItem>
        </S.TileMenu>
      )}

      {editedWithAi && (
        <S.AiTag data-testid={CASE_IMAGES_TEST_IDS.TILE_AI_TAG}>
          <Icon icon="sparkles" fill="white" width="12px" height="12px" />
          {t('caseImages.tile.editedWithAi')}
        </S.AiTag>
      )}

      {isDefault && (
        <S.DefaultCheck
          $nudged={editedWithAi}
          title={t('caseImages.tile.defaultImage')}
          data-testid={CASE_IMAGES_TEST_IDS.TILE_DEFAULT_CHECK}
        >
          <Icon icon="check" fill="white" width="12px" height="8px" />
        </S.DefaultCheck>
      )}
    </S.Tile>
  )
}
